import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Collapse
} from '@mui/material';
import { Search, AutoAwesome, Info, Description } from '@mui/icons-material';

const DiscoveryDialog = ({ open, onClose, onSearch }) => {
  const [jobTitle, setJobTitle] = useState('');
  const [experience, setExperience] = useState('');
  const [skills, setSkills] = useState('');
  const [requireJD, setRequireJD] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!jobTitle.trim()) {
      setError('Please enter a job title');
      return;
    }
    
    // If JD toggle is ON, validate JD text
    if (requireJD && !jobDescription.trim()) {
      setError('Please enter job description or turn off the toggle');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      await onSearch({
        job_title: jobTitle,
        experience: experience || '',
        skills: skills,
        require_jd: requireJD,
        job_description: jobDescription  // ← NEW: Send JD text
      });
      onClose();
      // Reset form
      setJobTitle('');
      setExperience('');
      setSkills('');
      setRequireJD(false);
      setJobDescription('');
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
      setRequireJD(false);
      setJobDescription('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search />
          <Typography variant="h6">Discover Candidates</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        <TextField
          fullWidth
          label="Job Title *"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          margin="normal"
          required
          placeholder="e.g., Senior React Developer"
          disabled={loading}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel>Experience (Years)</InputLabel>
          <Select
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            label="Experience (Years)"
            disabled={loading}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="1">1+ years</MenuItem>
            <MenuItem value="2">2+ years</MenuItem>
            <MenuItem value="3">3+ years</MenuItem>
            <MenuItem value="4">4+ years</MenuItem>
            <MenuItem value="5">5+ years</MenuItem>
            <MenuItem value="6">6+ years</MenuItem>
            <MenuItem value="7">7+ years</MenuItem>
            <MenuItem value="8">8+ years</MenuItem>
            <MenuItem value="9">9+ years</MenuItem>
            <MenuItem value="10">10+ years</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          label="Skills (comma-separated)"
          value={skills}
          onChange={(e) => setSkills(e.target.value)}
          margin="normal"
          placeholder="e.g., React, Node.js, MongoDB, TypeScript"
          disabled={loading}
          helperText="Enter skills separated by commas"
        />
        
        <Box sx={{ mt: 2, mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={requireJD}
                onChange={(e) => {
                  setRequireJD(e.target.checked);
                  if (!e.target.checked) {
                    setJobDescription(''); // Clear JD when toggle is OFF
                  }
                }}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Description fontSize="small" />
                <Typography>Include Job Description in Search</Typography>
                <Info fontSize="small" color="action" />
              </Box>
            }
          />
          
          {/* JD Text Field - Only shows when toggle is ON */}
          <Collapse in={requireJD}>
            <TextField
              fullWidth
              label="Job Description"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              margin="normal"
              multiline
              rows={4}
              placeholder="Paste the job description here... This will be used to find candidates whose profiles match the job requirements."
              disabled={loading}
              helperText="The job description text will be included in the search query to find better matching candidates"
              sx={{ mt: 1 }}
            />
          </Collapse>
        </Box>

        {/* Search Preview - Updated to show JD text */}
        {/* <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            🔍 Search Preview:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-word' }}>
            <strong>"{jobTitle || 'Job Title'}"</strong>
            {experience && <strong> "{experience}+ years"</strong>}
            {skills && ` (${skills.split(',').slice(0,3).join(' OR ')}${skills.split(',').length > 3 ? '...' : ''})`}
            <br/>
            (resume | cv | profile) (linkedin.com | github.com)
            {requireJD && jobDescription && (
              <>
                <br/>
                <strong style={{ color: '#ff9800' }}>"{jobDescription.substring(0, 100)}{jobDescription.length > 100 ? '...' : ''}"</strong>
              </>
            )}
          </Typography>
        </Box> */}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          💡 <strong>Tip:</strong> Including job description helps find candidates whose profiles closely match your requirements.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !jobTitle.trim() || (requireJD && !jobDescription.trim())}
          startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
          sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
        >
          {loading ? 'Searching...' : 'Search Candidates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscoveryDialog;